import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { useBriefActions, type BriefDetail } from "@/lib/briefs";

/**
 * Submit (SPEC §4). The readiness line is information, never a gate; the copy
 * says we will discuss the brief, not that it will be built. Any number of resubmissions.
 */
export function SubmitDialog({ detail, open, onClose }: { detail: BriefDetail; open: boolean; onClose: () => void }) {
  const { brief, sections } = detail;
  const actions = useBriefActions(brief.id);
  const [done, setDone] = useState<{ version: number } | null>(null);

  const empty = sections.filter((s) => s.coreKey && s.state === "empty").length;
  const notSure = sections.filter((s) => s.coreKey && s.state === "not_sure").length;
  const resubmit = brief.submissionCount > 0;

  const close = () => {
    setDone(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} title={done ? "Submitted" : resubmit ? "Resubmit this brief?" : "Submit this brief?"}>
      {done ? (
        <div className="text-sm">
          <p>
            Sent as <span className="font-medium">version {done.version}</span>. We have it now, and you have a
            copy under Versions. You can keep editing and resubmit any time.
          </p>
          <div className="mt-5 flex justify-end">
            <Button onClick={close}>Close</Button>
          </div>
        </div>
      ) : (
        <div className="text-sm">
          <p>
            <span className="font-medium">
              {brief.clear} of {brief.core} boxes clear
            </span>
            {notSure > 0 ? `, ${notSure} marked not sure` : ""}
            {empty > 0 ? `, ${empty} empty` : ""}.{" "}
            {empty > 0 || notSure > 0
              ? "A brief with gaps is still a good start; the rest can be worked out later."
              : "Looks ready."}
          </p>
          {resubmit ? (
            <p className="text-muted-foreground mt-2">
              This becomes version {brief.submissionCount + 1}. The earlier versions are kept too.
            </p>
          ) : null}
          <p className="text-muted-foreground mt-2">
            We'll go through it with you. Submitting starts a conversation; it is not a commitment to
            build anything.
          </p>
          {actions.submit.isError ? (
            <p className="text-destructive mt-2">{(actions.submit.error as Error).message}</p>
          ) : null}
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="outline" onClick={close} disabled={actions.submit.isPending}>
              Not yet
            </Button>
            <Button
              disabled={actions.submit.isPending}
              onClick={() => actions.submit.mutate(undefined, { onSuccess: (r) => setDone({ version: r.version }) })}
            >
              {actions.submit.isPending ? "Sending…" : resubmit ? "Resubmit" : "Submit"}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
